import { useDispatch } from "react-redux";
import {
  fetchMatterDetail,
  fetchMatterDetailList,
  updateCount,
} from "../slice/MatterDetailsSlice";
import { MS_FILE_TYPES, PDF_FILE_TYPES, EMAIL_FILE_TYPES } from "./constants";
import { getMatterIdFromPath, isCurrentMatter, resolveMatterId } from "./matterIdentity";
import { buildResume } from "./uploadTrayStorage";
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';

// Helper functions for file tree traversal
export const processFile = (file, path, allFiles) => {
  file.customRelativePath = path + file.name;
  allFiles.push(file);
};

export const getFileFromItem = (item, path, allFiles) => {
  return new Promise((resolve) => {
    item.file((file) => {
      processFile(file, path, allFiles);
      resolve();
    });
  });
};

export const readDirectoryEntries = (dirReader) => {
  return new Promise((resolve) => {
    dirReader.readEntries((entries) => resolve(entries));
  });
};


// Detects a zip file by extension or by MIME type. Browsers report zip MIME
// inconsistently across OSes (`application/zip`, `application/x-zip-compressed`,
// or even `application/octet-stream` on some Linux setups), so we trust the
// `.zip` extension first and fall back to a MIME hint.
export const isZipFile = (file) => {
  if (!file || !file.name) return false;
  if (/\.zip$/i.test(file.name)) return true;
  const t = (file.type || "").toLowerCase();
  return t === "application/zip" || t === "application/x-zip-compressed";
};


// Extract every PDF inside one or more zip files into the wrapped-file shape
// the upload pipeline expects (`{ file, customRelativePath }`). Folder
// structure inside the zip is preserved — each zip becomes a top-level folder
// named after the zip (without the `.zip` suffix) so it lands in DMS exactly
// like the equivalent "Upload Folder(s)" pick would.
//
// Skips:
//   * directory entries (JSZip surfaces them as `entry.dir === true`)
//   * non-PDF entries — claim-file zips often contain manifest CSVs, image
//     thumbnails, or `__MACOSX/` resource forks; the AI Upload pipeline only
//     consumes PDFs, so silently dropping non-PDFs is the right default
//   * `__MACOSX/` resource-fork directories (added by macOS Finder zips)
//   * empty PDF entries (corrupt or zero-byte)
//
// Throws on:
//   * a corrupt zip (`JSZip.loadAsync` throws — bubbles up so the caller can
//     surface "couldn't read claim_file_03.zip" to the user)
//
// Returns `{ files, folderNames, skipped }`:
//   * `files` — wrapped files ready to pass into `handleUpload`
//   * `folderNames` — top-level folder names, suitable for the third
//     argument of `handleUpload(...)` so it routes through the folder-upload
//     code path (which honors `customRelativePath` for nested folder
//     creation)
//   * `skipped` — count of non-PDF entries we dropped, useful for surfacing
//     a "x non-PDF entries skipped" hint to the user
export const extractZipsForUpload = async (zipFiles) => {
  const files = [];
  const folderNames = [];
  let skipped = 0;

  for (const zipFile of zipFiles) {
    const folderName = zipFile.name.replace(/\.zip$/i, "");
    if (folderName && !folderNames.includes(folderName)) {
      folderNames.push(folderName);
    }

    const zip = await JSZip.loadAsync(zipFile);

    const entries = Object.values(zip.files);
    for (const entry of entries) {
      if (entry.dir) continue;
      // Normalize separators (some Windows-built zips use backslashes) and
      // skip macOS resource forks.
      const normalized = entry.name.replace(/\\/g, "/");
      if (normalized.startsWith("__MACOSX/")) continue;
      if (!/\.pdf$/i.test(normalized)) {
        skipped += 1;
        continue;
      }

      const blob = await entry.async("blob");
      if (!blob || blob.size === 0) {
        skipped += 1;
        continue;
      }

      const baseName = normalized.split("/").pop();
      const pdfFile = new File([blob], baseName, {
        type: "application/pdf",
        lastModified: entry.date ? entry.date.getTime() : Date.now(),
      });

      // The path the upload pipeline uses to (a) decide which subfolders
      // need creating and (b) place the resulting Document. Prefixing with
      // `folderName` so multiple zips dropped at once don't collide.
      const customRelativePath = `${folderName}/${normalized}`;
      files.push({ file: pdfFile, customRelativePath });
    }
  }

  return { files, folderNames, skipped };
};


// Legacy binary Office formats (.doc/.xls) are OLE compound files, not
// zips — JSZip can't open them. Recognize them by the 8-byte OLE header.
const LEGACY_OFFICE_EXTENSIONS = ["doc", "xls"];
const OLE_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

export const validateMSFile = async (file) => {
  try {
    // 1️⃣ Empty file
    if (!file || file.size === 0) {
      return {
        isValid: false,
        reason: 'empty_file',
        message: 'Empty File / 0 KB'
      };
    }

    const extension = String(file.name || "")
      .split(".")
      .pop()
      .toLowerCase();
    if (LEGACY_OFFICE_EXTENSIONS.includes(extension)) {
      // file.slice().arrayBuffer() is unavailable in some environments
      // (jsdom); read the whole file and inspect the first bytes instead.
      const head = new Uint8Array(await file.arrayBuffer()).slice(
        0,
        OLE_SIGNATURE.length
      );
      const isOle =
        head.length === OLE_SIGNATURE.length &&
        OLE_SIGNATURE.every((byte, i) => head[i] === byte);
      if (!isOle) {
        return {
          isValid: false,
          reason: 'corrupted',
          message: 'Invalid File Format'
        };
      }
      return { isValid: true };
    }

    let zip;
    try {
      zip = await JSZip.loadAsync(await file.arrayBuffer());
    } catch {
      // 2️⃣ Corrupted / not an Office ZIP
      return {
        isValid: false,
        reason: 'corrupted',
        message: 'Invalid File Format'
      };
    }

    // 3️⃣ REAL password-protected (encrypted)
    if (
      zip.file('EncryptedPackage') ||
      zip.file('EncryptionInfo')
    ) {
      return {
        isValid: false,
        reason: 'password_protected',
        message: 'Password Protected'
      };
    }

    // 4️⃣ Must have Office structure
    if (!zip.file('[Content_Types].xml')) {
      return {
        isValid: false,
        reason: 'corrupted',
        message: 'Invalid File Format'
      };
    }

    // ✅ Valid Office file
    return { isValid: true };

  } catch (error) {
    console.error(error);
    return {
      isValid: false,
      reason: 'validation_error',
      message: 'Invalid File Format'
    };
  }
};



// Files above this size skip the in-browser full pdf.js parse during
// pre-upload validation (see the comment inside validatePDFFile). 50MB
// keeps deep validation for the common case while big scanned records
// upload without being buffered wholesale into browser memory.
export const FULL_PARSE_VALIDATION_MAX_BYTES = 50 * 1024 * 1024;

// How far back from the end of a PDF to look for the `%%EOF` marker. The
// marker is normally within the last few dozen bytes; the generous window
// tolerates trailing whitespace/junk some generators append. Larger than
// this and the file is not a PDF that any reader would open to the end.
export const PDF_TAIL_WINDOW_BYTES = 16 * 1024;
// Below this size the file is not a cut-off scan — it is either a complete
// tiny PDF or not a PDF at all, and the full pdf.js parse below settles that.
// The tail check is for the real failure shape: a multi-MB download that
// stopped early.
export const PDF_TAIL_CHECK_MIN_BYTES = 1024;
export const TRUNCATED_PDF_MESSAGE =
  'File is incomplete — the download was cut off before the end. Download it again, then upload it.';

/**
 * Cheap truncation check: does the file end with a PDF trailer (`%%EOF`)?
 * Reads only the last `PDF_TAIL_WINDOW_BYTES`. Resolves `{ ok: true }` when
 * the marker is present or the check itself could not run (never block an
 * upload on our own inability to read the slice).
 */
export const checkPdfTail = async (file) => {
  try {
    if (!file || typeof file.slice !== 'function' || !file.size) {
      return { ok: true, skipped: true };
    }
    if (file.size < PDF_TAIL_CHECK_MIN_BYTES) {
      return { ok: true, skipped: true };
    }
    const start = Math.max(0, file.size - PDF_TAIL_WINDOW_BYTES);
    const tail = await file.slice(start, file.size).arrayBuffer();
    const text = new TextDecoder('latin1').decode(new Uint8Array(tail));
    return { ok: text.includes('%%EOF') };
  } catch (error) {
    console.warn('Could not read PDF tail; allowing upload:', error);
    return { ok: true, skipped: true };
  }
};

export const validatePDFFile = async (file) => {
  try {
    // Check 1: File size is 0 KB
    if (!file || file.size === 0) {
      return {
        isValid: false,
        reason: 'empty_file',
        message: 'Empty File/ 0 KB'
      };
    }

    // Check 2: Read first 8 KB to check for password protection
    const firstChunk = file.slice(0, 8192);
    const arrayBuffer = await firstChunk.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const textDecoder = new TextDecoder('latin1');
    const text = textDecoder.decode(uint8Array);

    // Check for /Encrypt in PDF header (password protected)
    if (text.includes('/Encrypt')) {
      return {
        isValid: false,
        reason: 'password_protected',
        message: 'Password Protected'
      };
    }

    // Check 2b: the file actually ends. A PDF keeps its page index at the
    // very end, after the `%%EOF` marker; a download that was cut off
    // mid-transfer has the header, parses "well enough" for pdf.js, uploads
    // with a size the server has no way to dispute — and then dies hours
    // later in processing with "only part of this file arrived" (Billy
    // Plank 00300, 2026-10-06). Reading the tail is one tiny slice and
    // tells the user NOW, while the fix (re-download, re-upload) is cheap.
    const tailCheck = await checkPdfTail(file);
    if (!tailCheck.ok) {
      return {
        isValid: false,
        reason: 'truncated',
        message: TRUNCATED_PDF_MESSAGE,
      };
    }

    // Check 3: Try to parse the PDF to check for corruption.
    //
    // ONLY for reasonably-sized files. This reads the ENTIRE file into
    // an ArrayBuffer and runs a full pdf.js parse — on large scanned
    // records (e.g. a 106MB CAVC file, 2026-07-20) that step itself
    // dies in the browser (memory/parse), the file gets rejected before
    // presign, and the user sees a bare "Upload Failed" while 17
    // smaller siblings sail through. Large files keep the cheap header
    // checks above (empty / %PDF signature / /Encrypt) and defer deep
    // validation to the server, which validates on finalize anyway.
    if (file.size > FULL_PARSE_VALIDATION_MAX_BYTES) {
      return { isValid: true };
    }

    // Read the full file for parsing
    const fullArrayBuffer = await file.arrayBuffer();
    
    try {
      // Set worker source if not already set
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf-worker/pdf.worker.min.js`;
      }

      // Try to load and parse the PDF with a timeout
      const loadingTask = pdfjsLib.getDocument({
        data: fullArrayBuffer,
        verbosity: 0, // Suppress console warnings
        stopAtErrors: false, // Don't stop on minor errors
      });

      // Add timeout to prevent hanging on problematic PDFs
      const pdfPromise = loadingTask.promise;
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('PDF validation timeout')), 10000)
      );

      const pdf = await Promise.race([pdfPromise, timeoutPromise]);
      
      // Check if PDF is password protected (this catches cases where /Encrypt might be in trailer)
      if (pdf.isEncrypted) {
        return {
          isValid: false,
          reason: 'password_protected',
          message: 'Password Protected'
        };
      }

      // Try to get first page to ensure PDF is not corrupted
      // If this fails, we'll catch it and still allow upload (let server validate)
      try {
        await pdf.getPage(1);
      } catch (pageError) {
        // If we can't get the first page, log but don't fail - might be a PDF.js issue
        console.warn('Could not get first page, but PDF structure seems valid:', pageError);
        // Still return valid - let server do final validation
      }
      
      return { isValid: true };
    } catch (pdfError) {
      // If PDF.js throws an error, check what type of error
      console.warn('PDF validation error (may still be valid PDF):', pdfError);
      
      // Check if error is specifically about password
      if (pdfError.name === 'PasswordException' || 
          pdfError.message?.includes('password') ||
          pdfError.message?.includes('encrypted')) {
        return {
          isValid: false,
          reason: 'password_protected',
          message: 'Password Protected'
        };
      }
      
      // Check if it's a timeout or network error - allow upload in these cases
      if (pdfError.message?.includes('timeout') || 
          pdfError.message?.includes('network') ||
          pdfError.message?.includes('worker')) {
        console.warn('PDF.js worker/network issue, allowing upload:', pdfError);
        // Allow upload - let server validate
        return { isValid: true };
      }
      
      // For other errors, check if file has PDF signature (basic check)
      // If it starts with %PDF, it's likely a valid PDF even if PDF.js can't parse it
      const textStart = text.substring(0, 4);
      if (textStart === '%PDF') {
        console.warn('PDF has valid signature but PDF.js failed, allowing upload');
        // Allow upload - let server validate
        return { isValid: true };
      }
      
      // Otherwise, it's likely corrupted or invalid format
      return {
        isValid: false,
        reason: 'corrupted',
        message: 'Invalid File Format'
      };
    }
  } catch (error) {
    console.error('PDF validation error:', error);
    return {
      isValid: false,
      reason: 'validation_error',
      message: 'Invalid File Format'
    };
  }
};

export const traverseFileTree = async (item, allFiles, path = "") => {
  if (item.isFile) {
    return getFileFromItem(item, path, allFiles);
  }

  if (item.isDirectory) {
    const dirReader = item.createReader();
    const entries = await readDirectoryEntries(dirReader);

    if (entries.length === 0) {
      // Empty folder → push placeholder
      allFiles.push({
        file: null, // no binary
        customRelativePath: path + item.name + "/",
        emptyFolderName: item.name,
      });
    } else {
      for (const entry of entries) {
        await traverseFileTree(entry, allFiles, path + item.name + "/");
      }
    }
  }
};

// Upload utility functions
export const createUploads = (files) => {
  const batchId = `batch-${Date.now()}`; // Create unique batch ID for this upload session
  const uploads = files.map((fileOrWrapped, index) => {
    const { file, name, webkitRelativePath, size, emptyFolderName } = normalizeFile(fileOrWrapped);

    return {
      id: `upload-${Date.now()}-${index}`,
      fileName: name || webkitRelativePath || emptyFolderName || "unknown",
      fileSize: emptyFolderName ? "(empty folder)" : size,
      progress: 0,
      file, // always the actual File object
      type: emptyFolderName ? "folder" : "document",
      status: "uploading",
      uploadIndex: index + 1,
      totalUploads: files.length,
      batchId: batchId,
    };
  });

  return uploads;
};

export const getUploadMatterId = (matterDetails, routeId) =>
  resolveMatterId({ matterDetails, routeId }) || matterDetails?.id || "";

/**
 * The matter's display name ("Billy Plank | 0888 | AFFF") as it stood on the
 * page the user started the upload from. Stamped onto every upload and
 * failure so the upload tray can say which client a file was for even after
 * the user has navigated somewhere else.
 */
export const getUploadMatterLabel = (matterDetails) =>
  matterDetails?.matter_displayName || matterDetails?.display_name || "";

export const getUploadFolderId = (uniqueId) => {
  if (!uniqueId) return null;
  const strId = String(uniqueId);
  return strId.startsWith("F") ? strId.substring(1) : strId;
};

export const createObjectURL = (file, newly_created, matterDetails, uniqueId, overwrite, batchId, metadata = {}) => {
  // Compute relative path from metadata if provided
  const relativePath = metadata.webkitRelativePath || metadata.customRelativePath || null;

  const idValue = uniqueId ? getUploadFolderId(uniqueId) : matterDetails.id;

  return {
    file, // the native File object, untouched
    newly_created,
    matter: getUploadMatterId(matterDetails),
    id: idValue,
    folder: !!uniqueId,
    relative_path: relativePath,
    override_existing: overwrite,
    batchId,
  };
};

/**
 * Everything needed to resend one file exactly where it was going: the File
 * blob, the matter and folder, its relative path inside a dropped folder
 * tree, the upload session id (so the server treats the resend as a
 * continuation of the same folder upload rather than a conflicting one),
 * and whether this session asked for AI processing.
 */
export const buildRetryContext = ({
  file,
  matterDetails,
  uniqueId,
  newly_created = false,
  relativePath = null,
  overrideExisting = false,
  batchId = null,
  aiProcess = false,
}) => {
  if (!file) return null;
  return {
    file,
    matterDetails,
    uniqueId,
    newly_created,
    relativePath,
    overrideExisting,
    batchId,
    aiProcess: Boolean(aiProcess),
  };
};

// Failures where resending the same bytes cannot help: the server already
// holds a file by that name (use "rename and retry" instead), or it will
// never accept this kind of file.
const NON_RETRYABLE_ERROR_TYPES = new Set(['Duplicate File', 'Unsupported File']);

export const isRetryableFailedUpload = (failedUpload) => {
  const ctx = failedUpload?.retryContext;
  if (!ctx?.file) return false;
  if (failedUpload?.type === 'folder') return false;
  if (NON_RETRYABLE_ERROR_TYPES.has(failedUpload?.errorType)) return false;
  return true;
};

  // Track upload completion for batching API calls
let uploadCompletionCounter = 0;
let totalUploads = 0;
const BATCH_SIZE = 5; // Changed from 25 to 5
// Large selections must not wait for one server request to prepare every file
// before the first browser transfer can start.  The large-batch path below
// prepares files independently with the same ten-file transfer ceiling.
export const LARGE_UPLOAD_PRESIGN_BATCH_SIZE = 10;
// Track document IDs for each batch to call completeUpload
let currentBatchDocumentIds = [];
// Track which document IDs have already been completed (to avoid duplicates in final batch)
let completedDocumentIds = new Set();

// Track uploaded and optimized document IDs for optimization flow
let uploadedDocIds = []; // All uploaded document IDs
let optimizedDocIds = []; // All optimized document IDs
let isUploadComplete = false; // Track if all uploads are complete
let optimizationMatterDetailsConfig = null; // Store config for matterDetails API calls
// Whether the CURRENT upload session came through the AI Claim File
// Upload route. Only that route may request pipeline processing on
// completion (product ruling 2026-07-22).
let sessionAiProcess = false;

// Export functions to manage upload/optimization state
export const getUploadedDocIds = () => uploadedDocIds;
export const getOptimizedDocIds = () => optimizedDocIds;
export const getIsUploadComplete = () => isUploadComplete;
export const resetUploadOptimizationState = () => {
  uploadedDocIds = [];
  optimizedDocIds = [];
  isUploadComplete = false;
  optimizationMatterDetailsConfig = null;
};

  // shared pagination refresh
export const runPagination = ({ dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId }) => {
    // Matter URLs use Salesforce ids (a0L...), not just digits. A digits-only
    // parse treated every live matter as "not on a matter page", so an in-flight
    // claim-file upload kept refreshing the original matter over the one the
    // user had switched to.
    const currentMatterId = getMatterIdFromPath(
      globalThis.location?.pathname,
      globalThis.location?.href,
    );
    if (!isCurrentMatter(currentMatterId, matterDetails, id)) {
      return;
    }

    const matterIdToUse = currentMatterId;

    const page = currentPage || 1;

    if (matterDetails?.sf_id) {
      dispatch(fetchMatterDetail(matterDetails.id));
    } else {
      dispatch(fetchMatterDetail(matterIdToUse));
    }

    // Get current folder from URL if available
    const getCurrentFolder = () => {
      try {
        const pathname = globalThis.location?.pathname || '';
        const parts = pathname.split('/matter/')[1]?.split('/') || [];
        const lastPart = parts.at(-1);
        return lastPart?.startsWith('F') ? lastPart : null;
      } catch (error) {
        return null;
      }
    };

    const currentFolder = getCurrentFolder();
    const folderToUse = currentFolder || uniqueId;

    if (parentId !== undefined) {
      dispatch(fetchMatterDetailList({ id: matterIdToUse, page, folder: parentId }));
    } else if (folderToUse) {
      dispatch(fetchMatterDetailList({ id: matterIdToUse, page, folder: folderToUse }));
    } else {
      dispatch(fetchMatterDetailList({ id: matterIdToUse, page }));
    }

    if (currentPage) {
      setCurrentPage(currentPage);
    }
  };

  // success handler
  export const handleUploadSuccess = async (config) => {
    const {
      uploadId,
      updateUpload,
      dispatch,
      matterDetails,
      id,
      uniqueId,
      currentPage,
      setCurrentPage,
      parentId,
      documentId, // Add documentId to config
      isOfficeFile = false, // Flag to indicate if this is an MS Office file (DOCX, XLSX)
      markUploadCompleted,
    } = config;

    // Mark completed synchronously - so cancel shows as "uploaded" if user cancels after upload finished
    markUploadCompleted?.(uploadId);

    updateUpload(uploadId, { progress: 100, status: "completed" });

    uploadCompletionCounter++;
    
    // Track document ID for this upload
    if (documentId) {
      currentBatchDocumentIds.push(documentId);
      // Also ensure it's in uploadedDocIds (in case it wasn't added yet)
      if (!uploadedDocIds.includes(documentId)) {
        uploadedDocIds.push(documentId);
        console.log(`📝 Added document ID ${documentId} to uploadedDocIds. Total: ${uploadedDocIds.length}`);
      }
      
      // MS Office files (DOCX, XLSX) don't need optimization - add them to optimized list immediately
      if (isOfficeFile) {
        const normalizeDocId = (id) => {
          if (typeof id === 'string' && id.startsWith('D')) {
            return id.substring(1);
          }
          return String(id);
        };
        
        const normalizedDocId = normalizeDocId(documentId);
        const normalizedOptimizedIds = optimizedDocIds.map(id => normalizeDocId(id));
        
        if (!normalizedOptimizedIds.includes(normalizedDocId)) {
          optimizedDocIds.push(documentId);
          console.log(`✅ MS Office file ${documentId} added to optimized list (no optimization needed). Total optimized: ${optimizedDocIds.length}/${uploadedDocIds.length}`);
          
          // Check if we should call matterDetails APIs (same logic as PDF optimization)
          const totalUploaded = uploadedDocIds.length;
          const totalOptimized = optimizedDocIds.length;
          
          let shouldCallMatterDetails = false;
          
          if (totalUploaded >= 100) {
            // Call after every 25 optimized files
            if (totalOptimized % 25 === 0 && totalOptimized > 0) {
              shouldCallMatterDetails = true;
              console.log(`📋 Triggering matterDetails APIs: ${totalOptimized} optimized files (every 25th)`);
            }
          } else if (totalUploaded < 25) {
            // Call when all files are optimized
            if (totalOptimized === totalUploaded && totalOptimized > 0) {
              shouldCallMatterDetails = true;
              console.log(`📋 Triggering matterDetails APIs: All ${totalOptimized} files optimized`);
            }
          } else {
            // For files between 25 and 100, also call after every 25 optimized files
            if (totalOptimized % 25 === 0 && totalOptimized > 0) {
              shouldCallMatterDetails = true;
              console.log(`📋 Triggering matterDetails APIs: ${totalOptimized} optimized files (every 25th)`);
            }
          }
          
          // Call matterDetails APIs if needed
          if (shouldCallMatterDetails && optimizationMatterDetailsConfig) {
            const { dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId } = optimizationMatterDetailsConfig;
            console.log(`📋 Calling matterDetails and matterDetailsList APIs...`);
            runPagination({ dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId });
          }
        }
      }
    }
    
    console.log(`📤 Upload ${uploadCompletionCounter} completed. Document IDs in batch: ${currentBatchDocumentIds.length}`);
    
    // Store config for later matterDetails API calls (after optimization)
    if (!optimizationMatterDetailsConfig) {
      optimizationMatterDetailsConfig = { dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId };
    }
    
    // After every 5 upload completions: call completeUpload API only (no matterDetails API calls)
    // Only if total files >= 5 (if less than 5, wait until all uploads complete)
    if (totalUploads >= BATCH_SIZE && uploadCompletionCounter % BATCH_SIZE === 0) {
      const batchDocumentIds = [...currentBatchDocumentIds];
      currentBatchDocumentIds = []; // Reset for next batch
      
      // Mark these IDs as completed
      batchDocumentIds.forEach(id => completedDocumentIds.add(id));
      
      console.log(`✅ Batch complete (${uploadCompletionCounter} uploads). Calling completeUpload with ${batchDocumentIds.length} document IDs...`);
      
      try {
        // Call completeUpload API with document IDs from this batch
        // Do NOT call matterDetails/matterDetailsList APIs here - wait for optimization events
        const { completeUpload } = await import('../service/uploadService');
        await completeUpload(batchDocumentIds, { aiProcess: sessionAiProcess });
        console.log(`✅ CompleteUpload API called successfully for batch. Document IDs:`, batchDocumentIds);
        console.log(`⏳ Waiting for optimization events before calling matterDetails APIs...`);
      } catch (error) {
        console.error('❌ Error calling completeUpload API:', error);
      }
    }
  };

  // Handle optimization event from WebSocket
  export const handleOptimizationEvent = (documentId) => {
    // Normalize document ID (handle both string and number, and strip "D" prefix if present)
    const normalizeDocId = (id) => {
      if (typeof id === 'string' && id.startsWith('D')) {
        return id.substring(1);
      }
      return String(id);
    };
    
    const normalizedDocId = normalizeDocId(documentId);
    const normalizedUploadedIds = uploadedDocIds.map(id => normalizeDocId(id));
    
    // Verify that the document ID exists in uploadedDocIds
    if (!normalizedUploadedIds.includes(normalizedDocId)) {
      console.warn(`⚠️ Received optimization event for document ID ${documentId} (normalized: ${normalizedDocId}) that is not in uploadedDocIds. Ignoring.`);
      console.warn(`   Uploaded IDs:`, uploadedDocIds);
      return;
    }

    // Add to optimizedDocIds if not already present (using original format)
    const normalizedOptimizedIds = optimizedDocIds.map(id => normalizeDocId(id));
    if (!normalizedOptimizedIds.includes(normalizedDocId)) {
      optimizedDocIds.push(documentId); // Store in original format
      console.log(`✅ Document ${documentId} optimized. Total optimized: ${optimizedDocIds.length}/${uploadedDocIds.length}`);
      console.log(`   Optimized IDs:`, optimizedDocIds);
      console.log(`   Uploaded IDs:`, uploadedDocIds);
    } else {
      console.log(`⚠️ Document ${documentId} already in optimizedDocIds. Skipping.`);
    }

    // Check if we should call matterDetails APIs
    const totalUploaded = uploadedDocIds.length;
    const totalOptimized = optimizedDocIds.length;

    // Matter Details API Trigger Logic:
    // - If total uploaded files >= 100: call after every 25 optimized files
    // - If total uploaded files < 25: call when optimized files count equals total uploaded files
    let shouldCallMatterDetails = false;

    if (totalUploaded >= 100) {
      // Call after every 25 optimized files
      if (totalOptimized % 25 === 0 && totalOptimized > 0) {
        shouldCallMatterDetails = true;
        console.log(`📋 Triggering matterDetails APIs: ${totalOptimized} optimized files (every 25th)`);
      }
    } else if (totalUploaded < 25) {
      // Call when all files are optimized
      if (totalOptimized === totalUploaded && totalOptimized > 0) {
        shouldCallMatterDetails = true;
        console.log(`📋 Triggering matterDetails APIs: All ${totalOptimized} files optimized`);
      }
    } else {
      // For files between 25 and 100, also call after every 25 optimized files
      if (totalOptimized % 25 === 0 && totalOptimized > 0) {
        shouldCallMatterDetails = true;
        console.log(`📋 Triggering matterDetails APIs: ${totalOptimized} optimized files (every 25th)`);
      }
    }

    // Call matterDetails APIs if needed
    if (shouldCallMatterDetails && optimizationMatterDetailsConfig) {
      const { dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId } = optimizationMatterDetailsConfig;
      console.log(`📋 Calling matterDetails and matterDetailsList APIs...`);
      runPagination({ dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId });
    }

    // If all files are optimized, we can optionally clear the state (or keep it for reference)
    if (totalOptimized === totalUploaded && isUploadComplete) {
      console.log(`🎉 All ${totalOptimized} files uploaded and optimized!`);
    }
  };

  export const handleUploadFailure = (config) => {
    const {
      error,
      uploadId,
      addFailedUpload,
      removeUpload,
      setisLoading,
      uploadInfo,
      dispatch,
      matterDetails,
      id,
      uniqueId,
      currentPage,
      setCurrentPage,
      parentId,
      // Explicit errorType/retryContext win when the caller already knows
      // them (the partition-rejection path below does); otherwise fall back
      // to whatever uploadSingleFileWithSignedUrl attached to the thrown
      // Error, so every failure path gets the same "what kind of failure is
      // this" signal without needing its own plumbing.
      errorType,
      retryContext,
    } = config;

    const isAbortError = error?.name === "AbortError" || error?.message?.includes("aborted");
    const isPartialSuccess = error?.isPartialSuccess; // 👈 from our custom throw

    if (!isAbortError) {
      let uploadStatus = "failed";
      const errorMessage = extractErrorMessage(error);
      const resolvedErrorType = errorType ?? error?.errorType ?? null;

      addFailedUpload({
        id: uploadId,
        fileName: uploadInfo?.fileName || "Unknown file",
        fileSize: uploadInfo?.fileSize ?? 0,
        type: uploadInfo?.type,
        status: uploadStatus,
        // Which client this was for — read from the page the upload started
        // on, not whatever page the user is on when the failure lands.
        matterId: uploadInfo?.matterId || getUploadMatterId(matterDetails, id) || undefined,
        matterLabel: uploadInfo?.matterLabel || getUploadMatterLabel(matterDetails) || undefined,
        error: errorMessage,
        errorType: resolvedErrorType,
        // Present whenever we still hold the original File blob and the
        // upload parameters (matter, folder, relative path, upload session,
        // AI marker). The Failed tab uses it for one-click "Retry" and for
        // "rename and retry" on duplicate-name rejections. Absent only when
        // the file itself is the problem (client validation failures) —
        // retrying the same bytes would fail the same way.
        retryContext: retryContext ?? error?.retryContext ?? null,
      });
    }

    removeUpload(uploadId);

    if (setisLoading) {
      setisLoading(false);
    }

    // 👇 still run pagination if partial success
    if (isPartialSuccess) {
      runPagination({ dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId });
    }
  };

const processLargeUploadIndividually = async ({
  fileMetadata,
  fileUploadData,
  folderDetailsArray,
  emptyFolders,
  uploadMatterId,
  uploadFolderId,
  folderOverrideExisting,
  deferClientValidation,
  matterDetails,
  id,
  uniqueId,
  updateUpload,
  uploads,
  addUploadAbortController,
  handleUploadSuccess,
  handleUploadFailure,
  removeUpload,
  cancelUpload,
  addFailedUpload,
  setErrorToastMsgAndShow,
  setisLoading,
  currentPage,
  setCurrentPage,
  dispatch,
  parentId,
  markUploadCompleted,
  isUploadCancelled,
  aiProcess,
}) => {
  const { generateSignedUrls, completeUpload } = await import('../service/uploadService');
  const uploadPromises = [];
  const activeUploads = [];
  const failures = [];

  // Keep ten transfer/preparation slots active for this selection. The first
  // ten files therefore begin independently instead of waiting for a
  // whole 300+ file metadata request to finish.
  const startUploadWithLimit = async (uploadPromiseFactory) => {
    while (activeUploads.length >= LARGE_UPLOAD_PRESIGN_BATCH_SIZE) {
      await Promise.race(activeUploads);
    }

    const uploadPromise = uploadPromiseFactory();
    const removeFromActive = () => {
      const index = activeUploads.indexOf(uploadPromise);
      if (index > -1) {
        activeUploads.splice(index, 1);
      }
    };

    uploadPromise.finally(removeFromActive);
    activeUploads.push(uploadPromise);
    return uploadPromise;
  };

  // Folder-only requests are intentionally kept separate from file
  // preparation. They are small, create the folder shells first, and prevent
  // every individual file request from trying to replace the same folder.
  let fileOverrideExisting = fileMetadata[0]?.override_existing ?? false;
  if (folderDetailsArray.length > 0) {
    try {
      const folderResponse = await generateSignedUrls({
        presigned_files: folderDetailsArray,
        matter: uploadMatterId,
        newly_created: fileMetadata[0]?.newly_created ?? false,
        override_existing: folderOverrideExisting,
        batchId: fileMetadata[0]?.batchId ?? null,
        folder: uploadFolderId || '',
      });

      if (!folderResponse?.data?.presigned_uploads) {
        throw new Error('Failed to create upload folders');
      }

      // Files with relative paths can safely reuse the folders created above.
      fileOverrideExisting = false;

      for (const emptyFolder of emptyFolders) {
        handleUploadSuccess({
          uploadId: emptyFolder.uploadId,
          updateUpload,
          dispatch,
          matterDetails,
          id,
          uniqueId,
          currentPage,
          setCurrentPage,
          parentId,
          markUploadCompleted,
          aiProcess,
        });
      }
    } catch (error) {
      // Let each file request attempt to create its own folder hierarchy if
      // the small folder setup request failed. This keeps a folder upload from
      // blocking all of its files behind one non-file error.
      console.warn('Could not prepare upload folders before file uploads:', error);
      fileOverrideExisting = folderOverrideExisting;
    }
  }

  const recordFailure = (error, uploadId, uploadInfo, retryContext = null) => {
    failures.push(error?.message || String(error) || 'Upload failed');
    handleUploadFailure({
      error,
      uploadId,
      uploads,
      addFailedUpload,
      removeUpload,
      cancelUpload,
      // A 300-file upload must not produce 300 separate toast messages.
      setErrorToastMsgAndShow: undefined,
      setisLoading,
      handleSnackbar: null,
      uploadInfo,
      dispatch,
      matterDetails,
      id,
      uniqueId,
      currentPage,
      setCurrentPage,
      parentId,
      retryContext,
    });
  };

  for (const { file, uploadId, uploadInfo, uploadParams } of fileUploadData) {
    if (isUploadCancelled?.(uploadId)) {
      continue;
    }

    const uploadPromise = startUploadWithLimit(async () => {
      const abortController = new AbortController();
      addUploadAbortController(uploadId, abortController);

      const onUploadProgress = (progressEvent) => {
        const loaded = progressEvent.loaded || 0;
        const total = progressEvent.total || file.size;
        if (total) {
          updateUpload(uploadId, { progress: Math.round((loaded * 100) / total) });
        }
      };

      try {
        // The old path validated every PDF one after another before asking
        // the server for any upload URL. For a large selection, validate only
        // inside the active slot so the first files can move immediately.
        if (deferClientValidation) {
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          const fileType = file.type?.toLowerCase() || '';
          const isPDF = PDF_FILE_TYPES.includes(fileExtension) || PDF_FILE_TYPES.includes(fileType);
          const isMSFile = MS_FILE_TYPES.includes(fileExtension) || MS_FILE_TYPES.includes(fileType);

          if (isPDF || isMSFile) {
            const validationResult = await (isPDF ? validatePDFFile : validateMSFile)(file);
            if (!validationResult.isValid) {
              recordFailure(
                new Error(validationResult.message || 'Invalid file format'),
                uploadId,
                uploadInfo,
              );
              return { success: false, documentId: null, uploadId };
            }
          }
        }

        const response = await uploadSingleFileWithSignedUrl({
          file,
          matterDetails,
          uniqueId,
          newly_created: uploadParams.newly_created ?? false,
          onUploadProgress,
          signal: abortController.signal,
          relativePath: uploadParams.relative_path,
          overrideExisting: fileOverrideExisting,
          batchId: uploadParams.batchId,
          aiProcess,
          // The normal completion batching remains the source of truth for
          // this upload session. The per-file helper only uploads the bytes.
          completeUploadAfterUpload: false,
        });
        const documentId = response?.documentId;
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const fileType = file.type?.toLowerCase() || '';
        const isOfficeFile = MS_FILE_TYPES.includes(fileExtension) || MS_FILE_TYPES.includes(fileType) ||
          fileType.includes('msword') ||
          fileType.includes('spreadsheetml') ||
          fileType.includes('wordprocessingml');

        if (!documentId) {
          throw new Error(`No document ID available to complete the upload for ${file.name}`);
        }

        await handleUploadSuccess({
          uploadId,
          updateUpload,
          dispatch,
          matterDetails,
          id,
          uniqueId,
          currentPage,
          setCurrentPage,
          parentId,
          documentId,
          isOfficeFile,
          markUploadCompleted,
          aiProcess,
        });

        return { success: true, documentId, uploadId };
      } catch (error) {
        // Whatever went wrong (network drop, server hiccup, rejected name),
        // we still have the bytes and know exactly where they were going —
        // so the Failed tab can resend this one file into the same folder
        // of the same upload session without the user re-picking anything.
        recordFailure(error, uploadId, uploadInfo, buildRetryContext({
          file,
          matterDetails,
          uniqueId,
          newly_created: uploadParams.newly_created ?? false,
          relativePath: uploadParams.relative_path,
          overrideExisting: fileOverrideExisting,
          batchId: uploadParams.batchId,
          aiProcess,
        }));
        return { success: false, documentId: null, uploadId };
      }
    });

    uploadPromises.push(uploadPromise);
  }

  const results = await Promise.allSettled(uploadPromises);
  const successfulDocumentIds = results
    .filter((result) => result.status === 'fulfilled' && result.value?.success && result.value.documentId)
    .map((result) => result.value.documentId);
  const allIds = [...new Set([...currentBatchDocumentIds, ...successfulDocumentIds])];

  allIds.forEach((documentId) => {
    if (!uploadedDocIds.includes(documentId)) {
      uploadedDocIds.push(documentId);
    }
  });

  isUploadComplete = true;
  const idsToComplete = allIds.filter((documentId) => !completedDocumentIds.has(documentId));
  currentBatchDocumentIds = [];
  completedDocumentIds.clear();

  if (idsToComplete.length > 0) {
    try {
      await completeUpload(idsToComplete, { aiProcess: sessionAiProcess });
      const { invalidateDocument } = await import('../service/documentCache');
      for (const documentId of allIds) {
        await invalidateDocument(String(documentId));
      }
    } catch (error) {
      console.error('Failed to complete large upload batch:', error);
    }
  }

  const totalUploaded = uploadedDocIds.length;
  const totalOptimized = optimizedDocIds.length;
  if (totalOptimized === totalUploaded && totalOptimized > 0 && optimizationMatterDetailsConfig) {
    const paginationConfig = optimizationMatterDetailsConfig;
    runPagination(paginationConfig);
  }

  if (failures.length > 0 && setErrorToastMsgAndShow) {
    setErrorToastMsgAndShow(`${failures.length} file${failures.length === 1 ? '' : 's'} failed to upload.`);
  }

  // Keep the legacy upload path available for callers that do not provide the
  // signed-URL helper. This branch is only selected for the new large-batch
  // path, where the hook is expected to provide it.
  return { allIds };
};


export const processFiles = async (config) => {
  const {
    files,
    createUploads,
    addUpload,
    setShowDragNotification,
    addUploadAbortController,
    uploadFile,
    uploadFileToSignedUrl,
    createObjectURL,
    handleUploadSuccess,
    handleUploadFailure,
    matterDetails,
    id,
    uniqueId,
    updateUpload,
    uploads,
    removeUpload,
    cancelUpload,
    addFailedUpload,
    setErrorToastMsgAndShow,
    setisLoading,
    currentPage,
    setCurrentPage,
    dispatch,
    parentId,
    overwrite,
    markUploadCompleted,
    clearCompletedUploadIds,
    isUploadCancelled,
    aiProcess = false,
  } = config;
  const uploadMatterId = getUploadMatterId(matterDetails, id);
  const uploadFolderId = getUploadFolderId(uniqueId);
  // The per-file transfer loop shadows `handleUploadFailure` with a wrapper
  // that injects the file's retry context; this keeps a handle on the real one.
  const reportUploadFailure = handleUploadFailure;

  // Clear completed IDs from previous upload session
  clearCompletedUploadIds?.();

  // Reset upload completion counter and initialize total uploads
  sessionAiProcess = Boolean(aiProcess);
  uploadCompletionCounter = 0;
  totalUploads = files.length; // Will be updated to include empty folders later
  currentBatchDocumentIds = []; // Reset document IDs tracking
  completedDocumentIds = new Set(); // Reset completed IDs tracking
  // Reset upload/optimization state for new upload session
  uploadedDocIds = [];
  optimizedDocIds = [];
  isUploadComplete = false;
  optimizationMatterDetailsConfig = null;

  const uploadMatterLabel = getUploadMatterLabel(matterDetails);
  const newUploads = createUploads(files).map((upload, index) => {
    const picked = normalizeFile(files[index]);
    return {
      ...upload,
      matterId: uploadMatterId || undefined,
      matterLabel: uploadMatterLabel || undefined,
      // Where this file is headed, kept without the file itself. If the page
      // reloads mid-upload the tray is rebuilt from this, so the file can be
      // picked again and sent to the same matter and folder (see
      // uploadTrayStorage.js). Empty-folder placeholders have nothing to resend.
      resume: upload.type === 'folder'
        ? undefined
        : buildResume({
            matterDetails,
            uniqueId,
            relativePath: picked.webkitRelativePath || picked.customRelativePath || null,
            overrideExisting: overwrite,
            batchId: upload.batchId,
            aiProcess,
          }),
    };
  });
  for (const upload of newUploads) {addUpload(upload)}

  if (setShowDragNotification) {
    setShowDragNotification(false);
  }

  // Prepare file metadata for signed URL generation
  // Validate PDFs before generating signed URLs
  const fileMetadata = [];
  const fileUploadData = [];
  const invalidPDFs = []; // Track invalid PDFs to mark as failed
  const emptyFolders = []; // Track empty folders to include in payload
  const deferClientValidation = files.length > LARGE_UPLOAD_PRESIGN_BATCH_SIZE;

  for (let index = 0; index < files.length; index++) {
    const fileOrWrapped = files[index];
    const normalized = normalizeFile(fileOrWrapped);
    
    // Check if this is an empty folder
    if (normalized.emptyFolderName) {
      emptyFolders.push({
        uploadId: newUploads[index].id,
        uploadInfo: newUploads[index],
        folderName: normalized.emptyFolderName,
        customRelativePath: normalized.customRelativePath,
        webkitRelativePath: normalized.webkitRelativePath,
      });
      continue; // Skip empty folders in file processing, handle them separately
    }
    
    const actualFile = normalized.file;
    if (!actualFile) {
      // If no file and not an empty folder, mark as invalid
      invalidPDFs.push({
        uploadId: newUploads[index].id,
        uploadInfo: newUploads[index],
        error: 'Invalid File Format',
        reason: 'Invalid File Format',
      });
      continue;
    }
    
    // Check if this is a PDF file - improved detection
    const fileName = actualFile.name || '';
    const fileExtension = fileName.includes('.') 
      ? fileName.split('.').pop()?.toLowerCase() 
      : '';
    const fileType = (actualFile.type || '').toLowerCase();
    
    // More robust PDF detection: check extension, MIME type, or file signature
    const isPDF = PDF_FILE_TYPES.includes(fileExtension) || 
                  PDF_FILE_TYPES.includes(fileType) ||
                  fileExtension === 'pdf' ||
                  fileType === 'application/pdf';
    
    const isMSFILES = MS_FILE_TYPES.includes(fileExtension) || 
                      MS_FILE_TYPES.includes(fileType);

    // Email files (.eml/.msg) are read-only documents - the backend stores
    // and previews them as-is, so there's no client-side format to validate
    // (validatePDFFile/validateMSFile only understand PDF and Office ZIP/OLE
    // structures and would misreport a well-formed email as corrupted).
    const isEmailFile = EMAIL_FILE_TYPES.includes(fileExtension) ||
                        EMAIL_FILE_TYPES.includes(fileType);
    
    // Validate small selections before adding metadata. For large selections,
    // validation is deferred into the ten active upload slots below so the
    // first files do not wait behind hundreds of sequential PDF parses.
    if (isPDF || isMSFILES || isEmailFile) {
      if (!deferClientValidation && (isPDF || isMSFILES)) {
        try {
          const validationResult = await (isPDF ? validatePDFFile : validateMSFile)(actualFile);

          if (!validationResult.isValid) {
            // Mark this PDF as failed - don't generate signed URL for it
            invalidPDFs.push({
              uploadId: newUploads[index].id,
              uploadInfo: newUploads[index],
              error: validationResult.message || 'Invalid PDF file',
              reason: validationResult.reason,
            });
            continue; // Skip this file, don't add to fileMetadata
          }
        } catch (validationError) {
          // If validation itself throws an error, log it but don't fail the upload
          // This handles cases where PDF.js might have issues but the file is still valid
          console.warn('Validation error for file:', actualFile.name, validationError);
          // Continue with upload - let the server handle validation if client validation fails
        }
      }

      const metadata = getFileMetadata(fileOrWrapped);
      const uploadParams = createObjectURL(actualFile, false, matterDetails, uniqueId, overwrite, newUploads[index].batchId, metadata);

      fileMetadata.push({
        filename: actualFile.name,
        size: actualFile.size,
        matter: uploadParams.matter,
        folder: uploadParams.folder,
        newly_created: uploadParams.newly_created,
        relative_path: uploadParams.relative_path,
        override_existing: uploadParams.override_existing,
        batchId: uploadParams.batchId,
      });

      fileUploadData.push({
        file: actualFile,
        uploadId: newUploads[index].id,
        uploadInfo: newUploads[index],
        uploadParams,
      });
    } else {
      invalidPDFs.push({
        uploadId: newUploads[index].id,
        uploadInfo: newUploads[index],
        error: 'Invalid File Format',
        reason: 'Invalid File Format',
      });
    }
  }

  // Mark invalid PDFs as failed immediately
  for (const invalidPDF of invalidPDFs) {
    handleUploadFailure({
      error: invalidPDF.error,
      uploadId: invalidPDF.uploadId,
      uploads,
      addFailedUpload,
      removeUpload,
      cancelUpload,
      setErrorToastMsgAndShow,
      setisLoading,
      handleSnackbar: null,
      uploadInfo: invalidPDF.uploadInfo,
      dispatch,
      matterDetails,
      id,
      uniqueId,
      currentPage,
      setCurrentPage,
      parentId,
    });
  }

  // Step 1: Generate signed URLs for all valid files (invalid PDFs already marked as failed)
  // If no valid files remain after validation, return early
  if (fileMetadata.length === 0 && emptyFolders.length === 0) {
    console.log('No valid files or folders to upload after validation');
    return;
  }

  // Extract folder details from relative paths and add them to presigned_files
  const folderDetailsMap = new Map(); // Track unique folders by their path
  const baseFolderId = uploadFolderId;

  // Process each file's relative_path to extract folder structure
  for (const fileMeta of fileMetadata) {
    if (fileMeta.relative_path) {
      const relativePath = fileMeta.relative_path;
      // Remove trailing slash and split into parts
      const pathParts = relativePath.replace(/\/$/, '').split('/').filter(Boolean);
      
      // Build folder paths incrementally (e.g., "Folder1", "Folder1/SubFolder")
      let currentPath = '';
      for (let i = 0; i < pathParts.length - 1; i++) { // Exclude the file name (last part)
        currentPath = currentPath ? `${currentPath}/${pathParts[i]}` : pathParts[i];
        const folderPath = currentPath + '/';
        
        // Only add if not already in map
        if (!folderDetailsMap.has(folderPath)) {
          // Determine parent folder ID
          let parentFolderId = baseFolderId;
          if (i > 0) {
            // This is a nested folder - find parent folder ID
            const parentPath = pathParts.slice(0, i).join('/') + '/';
            parentFolderId = folderDetailsMap.get(parentPath)?.folder_id || baseFolderId;
          }
          
          folderDetailsMap.set(folderPath, {
            folder_name: pathParts[i],
            folder_path: folderPath,
            parent_folder_id: parentFolderId,
            folder_id: null, // Will be set by backend response
          });
        }
      }
    }
  }

  // Process empty folders and add them to folderDetailsMap
  for (const emptyFolder of emptyFolders) {
    const relativePath = emptyFolder.customRelativePath || emptyFolder.webkitRelativePath || '';
    // Remove trailing slash and split into parts
    const pathParts = relativePath.replace(/\/$/, '').split('/').filter(Boolean);
    
    if (pathParts.length > 0) {
      // The last part is the empty folder name
      const folderName = pathParts.at(-1);
      const folderPath = relativePath.endsWith('/') ? relativePath : relativePath + '/';
      
      // Determine parent folder ID
      let parentFolderId = baseFolderId;
      if (pathParts.length > 1) {
        // This is a nested empty folder - find parent folder ID
        const parentPathParts = pathParts.slice(0, -1);
        const parentPath = parentPathParts.join('/') + '/';
        parentFolderId = folderDetailsMap.get(parentPath)?.folder_id || baseFolderId;
      }
      
      // Add empty folder to map (will overwrite if already exists from file paths)
      folderDetailsMap.set(folderPath, {
        folder_name: folderName,
        folder_path: folderPath,
        parent_folder_id: parentFolderId,
        folder_id: null, // Will be set by backend response
        is_empty_folder: true, // Mark as empty folder
      });
    }
  }

  // For empty-folder-only replacement: use override_existing: "folder" so folder_session_id is sent (same as folder-with-files)
  const isEmptyFolderOnlyReplace = fileMetadata.length === 0 && emptyFolders.length > 0 && overwrite;
  const folderOverrideExisting = isEmptyFolderOnlyReplace ? "folder" : (fileMetadata[0]?.override_existing ?? overwrite ?? false);

  // Convert folder details map to array, sort by depth (shallowest first), and add to presigned_files
  const folderDetailsArray = Array.from(folderDetailsMap.values())
    .sort((a, b) => {
      // Sort by path depth (shallowest first) to ensure parent folders are created before children
      const depthA = a.folder_path.split('/').filter(Boolean).length;
      const depthB = b.folder_path.split('/').filter(Boolean).length;
      return depthA - depthB;
    })
    .map(folder => ({
      type: 'folder',
      folder_name: folder.folder_name,
      folder_path: folder.folder_path,
      parent_folder_id: folder.parent_folder_id,
      is_empty_folder: folder.is_empty_folder || false,
      matter: uploadMatterId,
      newly_created: fileMetadata[0]?.newly_created ?? overwrite === false,
      override_existing: folderOverrideExisting,
      batchId: fileMetadata[0]?.batchId ?? newUploads[0]?.batchId ?? null,
    }));

  // Combine folder details and file metadata (folders first, then files)
  const presignedFiles = [...folderDetailsArray, ...fileMetadata];

  // If we only have empty folders (no files), we still need to process them
  if (presignedFiles.length === 0) {
    console.log('No files or folders to process');
    return;
  }

  if (fileMetadata.length > LARGE_UPLOAD_PRESIGN_BATCH_SIZE && uploadFileToSignedUrl) {
    await processLargeUploadIndividually({
      fileMetadata,
      fileUploadData,
      folderDetailsArray,
      emptyFolders,
      uploadMatterId,
      uploadFolderId,
      folderOverrideExisting,
      deferClientValidation,
      matterDetails,
      id,
      uniqueId,
      updateUpload,
      uploads,
      addUploadAbortController,
      handleUploadSuccess,
      handleUploadFailure,
      removeUpload,
      cancelUpload,
      addFailedUpload,
      setErrorToastMsgAndShow,
      setisLoading,
      currentPage,
      setCurrentPage,
      dispatch,
      parentId,
      markUploadCompleted,
      isUploadCancelled,
      aiProcess,
    });
    return;
  }

  let signedUrlsResponse;
  // Declared OUTSIDE the try so the post-catch `signedUrls = ...` block can read
  // it. When the backend partially rejected the batch, this holds the per-file
  // signed URLs in the same order as the (now-trimmed) `fileUploadData` so the
  // index-based upload loop below stays aligned. Stays `null` when the backend
  // accepted everything and we fall back to the original "filter all file-shaped
  // entries" code path.
  let signedUrlsByAcceptedIndex = null;
  try {
    const { generateSignedUrls } = await import('../service/uploadService');
    console.log("presignedFiles with folder details (including empty folders)", presignedFiles, "uniqueId", uniqueId);
    signedUrlsResponse = await generateSignedUrls({
      presigned_files: presignedFiles,
      matter: uploadMatterId,
      newly_created: fileMetadata[0]?.newly_created ?? (overwrite === false),
      override_existing: folderOverrideExisting,
      batchId: fileMetadata[0]?.batchId ?? newUploads[0]?.batchId ?? null,
      folder: uploadFolderId || ''
    });
    console.log("signedUrlResponse", signedUrlsResponse);
    // Validate response - should have entries for both folders and files
    if (!signedUrlsResponse?.data?.presigned_uploads) {
      throw new Error('Failed to get presigned uploads response');
    }
    
    // Separate folder responses from file responses
    const folderResponses = signedUrlsResponse.data.presigned_uploads.filter(
      (item) => item.type === 'folder' || (item.folder_id !== undefined && item.document_id === undefined)
    );
    const fileResponses = signedUrlsResponse.data.presigned_uploads.filter(
      (item) => item.type !== 'folder' && (item.document_id !== undefined || item.upload_url !== undefined)
    );
    
    // Update folder details map with folder IDs from response
   
    for (const folderResponse of folderResponses) {
      if (!folderResponse.folder_path) {
        continue;
      }
      const folderDetail = folderDetailsMap.get(folderResponse.folder_path);
      if (!folderDetail) {
        continue;
      }
      folderDetail.folder_id = folderResponse.folder_id ?? folderResponse.id;
    }
    
    // Update total uploads to include empty folders
    totalUploads = fileMetadata.length + emptyFolders.length;
    
    // Mark empty folder uploads as completed immediately after URL generation API succeeds
    // Folders are created by the backend during the presigned URL generation API call
    // We don't need to upload folders - they're already created, so mark them as completed
    // Mark folders as completed regardless of folder responses - if API succeeded, folders were created
    if (emptyFolders.length > 0) {
      console.log('Marking empty folders as completed after API success:', emptyFolders.length);
      // Mark all empty folder uploads as successful immediately
      // The backend has already created the folders during the API call
      for (const emptyFolder of emptyFolders) {
        console.log('Marking empty folder as completed:', emptyFolder.folderName, emptyFolder.uploadId);
        handleUploadSuccess({
          uploadId: emptyFolder.uploadId,
          updateUpload,
          dispatch,
          matterDetails,
          id,
          uniqueId,
          currentPage,
          setCurrentPage,
          parentId,
          markUploadCompleted,
        });
      }
    }
    
    // Reconcile what we requested with what the backend accepted.
    //
    // The backend returns HTTP 207 with `presigned_uploads` shorter than what we
    // asked for when SOME files in the batch are rejected (filename validation,
    // duplicates, unsupported file types, etc.). axios resolves on 2xx including
    // 207, so we land here on a partial success. Until this fix, we used to
    // `throw new Error(\`Expected N got M\`)` here, which (a) discarded every
    // per-file rejection reason the backend already computed and surfaced as
    // `errors` / `error_objects`, and (b) flipped EVERY file's row to "failed",
    // including the ones the backend was perfectly happy to upload. This is
    // exactly the "wall of red" the user reported on a 50-file claim-file batch
    // where 8 of those filenames contained an HTML-entity ampersand.
    //
    // Instead, we now: split the batch into accepted vs rejected based on
    // filename matching, mark each rejected row as failed with its specific
    // backend message, fire a single grouped summary toast (a per-file toast on
    // a 50-file upload would be unusable), and let the rest of the flow proceed
    // with only the accepted files. The mutation-in-place is intentional —
    // `fileUploadData` and `fileMetadata` are `const`, but they're arrays and
    // the downstream upload loop indexes into them in lockstep with `signedUrls`.
    if (fileMetadata.length > 0 && fileResponses.length !== fileMetadata.length) {
      const partition = partitionPresignedUploadResults(
        fileMetadata,
        fileResponses,
        signedUrlsResponse.data.error_objects,
        signedUrlsResponse.data.errors,
      );

      const rejections = partition.rejectedIndices.map((origIdx) => {
        const rej = partition.rejectionByIndex.get(origIdx) || {};
        const entry = fileUploadData[origIdx];
        const meta = fileMetadata[origIdx];
        return {
          uploadId: entry?.uploadId,
          uploadInfo: entry?.uploadInfo,
          filename: rej.filename || entry?.file?.name || '',
          message: rej.message,
          error_type: rej.error_type,
          // Duplicate-filename rejections are the one case where the user
          // has an obvious fix (pick a different name); everything else
          // gets a plain "Retry". Either way we still hold the original
          // File blob plus the exact params this file was about to upload
          // with. Shape matches uploadSingleFileWithSignedUrl's own params
          // so a retry can spread this straight in without translation. Use
          // the batch's own matterDetails/uniqueId (every file in this call
          // shares one destination) rather than `meta.matter`/`meta.folder`
          // — those are the resolved matter id string and a bare `folder:
          // !!uniqueId` boolean flag from createObjectURL, not the actual
          // folder id, so a retry into a subfolder was silently sent to
          // folder "true"/"false" instead of the real folder (caught by
          // Bugbot on #687, PR fix/rename-retry-folder-target).
          retryContext: buildRetryContext({
            file: entry?.file,
            matterDetails,
            uniqueId,
            newly_created: meta?.newly_created,
            relativePath: meta?.relative_path,
            overrideExisting: meta?.override_existing,
            batchId: meta?.batchId,
            aiProcess,
          }),
        };
      });

      // Mark each rejected file as failed individually, with its real reason,
      // so its row in the upload tray shows the per-file message instead of a
      // generic failure. We pass `setErrorToastMsgAndShow: undefined` so the
      // failure handler doesn't fire 8 separate toasts on a 50-file batch — we
      // fire ONE grouped summary toast below covering all rejections.
      for (const rej of rejections) {
        if (!rej.uploadId) continue;
        handleUploadFailure({
          error: new Error(rej.message),
          errorType: rej.error_type,
          retryContext: rej.retryContext,
          uploadId: rej.uploadId,
          uploads,
          addFailedUpload,
          removeUpload,
          cancelUpload,
          setErrorToastMsgAndShow: undefined,
          setisLoading,
          handleSnackbar: null,
          uploadInfo: rej.uploadInfo,
          dispatch,
          matterDetails,
          id,
          uniqueId,
          currentPage,
          setCurrentPage,
          parentId,
        });
      }

      if (rejections.length > 0 && setErrorToastMsgAndShow) {
        setErrorToastMsgAndShow(buildRejectionSummaryToast(rejections, fileMetadata.length));
      }

      // Mutate `fileUploadData` and `fileMetadata` in place to keep only the
      // accepted entries, in their original order. The downstream loop iterates
      // by index across both arrays + the signed-URL list, so trimming all three
      // in lockstep keeps the indexing contract intact.
      const acceptedFileUploadData = partition.acceptedIndices.map((i) => fileUploadData[i]);
      const acceptedFileMetadata = partition.acceptedIndices.map((i) => fileMetadata[i]);
      fileUploadData.splice(0, fileUploadData.length, ...acceptedFileUploadData);
      fileMetadata.splice(0, fileMetadata.length, ...acceptedFileMetadata);
      signedUrlsByAcceptedIndex = partition.orderedSignedUrlsForAccepted;

      // If every file in the batch was rejected, there's nothing left to upload.
      // Bail early so the rest of the flow doesn't run an empty upload loop +
      // call completeUpload([]) on the way out.
      if (fileUploadData.length === 0) {
        isUploadComplete = true;
        return;
      }
    }
    
    // If we only have empty folders, we're done after folder creation
    if (fileMetadata.length === 0 && (folderResponses.length > 0 || emptyFolders.length > 0)) {
      // Mark upload as complete so UI shows "Upload Complete" instead of infinite "Optimizing" loader
      // Empty folders have no document IDs, so uploadedDocIds stays empty - we must set this explicitly
      isUploadComplete = true;
      // Call final refresh APIs since all uploads (folders) are complete
      const remainingAfterLastBatch = uploadCompletionCounter % BATCH_SIZE;
      if (remainingAfterLastBatch > 0 || totalUploads < BATCH_SIZE) {
        await runPagination({ dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId });
      }
      return; // Exit early since there are no files to upload (folders already marked as completed above)
    }
  } catch (error) {
    // If signed URL generation fails, mark all uploads as failed (including empty folders)
    console.error('Failed to generate signed URLs:', error);
    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to generate signed URLs';
    for (const { file, uploadId, uploadInfo, uploadParams } of fileUploadData) {
      handleUploadFailure({
        error: errorMessage,
        retryContext: buildRetryContext({
          file,
          matterDetails,
          uniqueId,
          newly_created: uploadParams?.newly_created ?? false,
          relativePath: uploadParams?.relative_path,
          overrideExisting: uploadParams?.override_existing,
          batchId: uploadParams?.batchId,
          aiProcess,
        }),
        uploadId,
        uploads,
        addFailedUpload,
        removeUpload,
        cancelUpload,
        setErrorToastMsgAndShow,
        setisLoading,
        handleSnackbar: null,
        uploadInfo,
        dispatch,
        matterDetails,
        id,
        uniqueId,
        currentPage,
        setCurrentPage,
        parentId,
      });
    }
    // Also mark empty folders as failed when API fails (e.g. 400 on replace)
    for (const emptyFolder of emptyFolders) {
      handleUploadFailure({
        error: errorMessage,
        uploadId: emptyFolder.uploadId,
        uploads,
        addFailedUpload,
        removeUpload,
        cancelUpload,
        setErrorToastMsgAndShow,
        setisLoading,
        handleSnackbar: null,
        uploadInfo: emptyFolder.uploadInfo,
        dispatch,
        matterDetails,
        id,
        uniqueId,
        currentPage,
        setCurrentPage,
        parentId,
      });
    }
    return;
  }

  // Filter to get only file responses (exclude folder responses).
  //
  // When the backend partially rejected the batch, we already lined up signed
  // URLs with their accepted `fileUploadData` entries (by filename) above. Use
  // that pre-aligned list so the index-based loop below stays in lockstep with
  // the trimmed `fileUploadData`. When the backend accepted everything, we fall
  // back to the original "filter all file-shaped entries" behaviour.
  const signedUrls = Array.isArray(signedUrlsByAcceptedIndex)
    ? signedUrlsByAcceptedIndex
    : signedUrlsResponse.data.presigned_uploads.filter(
        (item) => item.type !== 'folder' && (item.document_id !== undefined || item.upload_url !== undefined)
      );

  // Step 2: Upload each file to its corresponding signed URL or SharePoint URL
  // Track upload promises and successful document IDs
  const uploadPromises = [];
  
  // Limit concurrent uploads to 10 to prevent browser freeze with large file counts (300+ files)
  const MAX_CONCURRENT_UPLOADS = 10;
  const activeUploads = [];
  
  // Helper to start an upload with concurrency limit
  const startUploadWithLimit = async (uploadPromiseFactory) => {
    // Wait if we've reached the concurrency limit
    while (activeUploads.length >= MAX_CONCURRENT_UPLOADS) {
      await Promise.race(activeUploads);
    }
    
    // Create and start the upload promise
    const uploadPromise = uploadPromiseFactory();
    
    // Track this upload
    const removeFromActive = () => {
      const index = activeUploads.indexOf(uploadPromise);
      if (index > -1) {
        activeUploads.splice(index, 1);
      }
    };
    
    // Remove from active when done (success or failure)
    uploadPromise.finally(removeFromActive);
    
    activeUploads.push(uploadPromise);
    return uploadPromise;
  };

  for (let index = 0; index < fileUploadData.length; index++) {
    const { file, uploadId, uploadInfo, uploadParams } = fileUploadData[index];
    // Skip if user cancelled before we got signed URLs (e.g. during folder upload API wait)
    if (isUploadCancelled?.(uploadId)) {
      continue;
    }
    const signedUrlData = signedUrls[index];
    // Get document ID from presigned response - it should be available in the response
    const documentId = signedUrlData.document_id || signedUrlData.id || signedUrlData.unique_id;
    
    // Check if this is a PDF or Office file
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const fileType = file.type?.toLowerCase() || '';
    const isPDF = PDF_FILE_TYPES.includes(fileExtension) || PDF_FILE_TYPES.includes(fileType);
    const isOfficeFile = MS_FILE_TYPES.includes(fileExtension) || MS_FILE_TYPES.includes(fileType) ||
                         fileType.includes('msword') || 
                         fileType.includes('spreadsheetml') ||
                         fileType.includes('wordprocessingml');
    
    const sharepointUrl = signedUrlData.upload_url || signedUrlData.sharepoint_web_url || signedUrlData.sharepoint_url;
    const signedUrl = signedUrlData.upload_url;
    const signedContentType = signedUrlData.content_type;

    const abortController = new AbortController();
    addUploadAbortController(uploadId, abortController);

    const onUploadProgress = (progressEvent) => {
      // Handle both axios progress event and custom progress object
      const loaded = progressEvent.loaded || 0;
      const total = progressEvent.total || file.size;
      if (total) {
        const progress = Math.round((loaded * 100) / total);
        updateUpload(uploadId, { progress });
      }
    };

    // Every transfer branch below reports failures through the same
    // handler; give them all the bytes + destination so the Failed tab can
    // offer a one-click Retry into the same folder of the same session.
    const retryContext = buildRetryContext({
      file,
      matterDetails,
      uniqueId,
      newly_created: uploadParams?.newly_created ?? false,
      relativePath: uploadParams?.relative_path,
      overrideExisting: uploadParams?.override_existing,
      batchId: uploadParams?.batchId,
      aiProcess,
    });
    const handleUploadFailure = (failureConfig) =>
      reportUploadFailure({ retryContext, ...failureConfig });

    // Use uploadFileToSignedUrl if available, otherwise fall back to legacy uploadFile
    if (uploadFileToSignedUrl) {
      let uploadPromise;
      
      if (isPDF && signedUrl) {
        // PDF files: upload to signed URL
        console.log("Uploading PDF to signed URL:", file.name);
        uploadPromise = startUploadWithLimit(() => 
          uploadFileToSignedUrl({ file, signedUrl, onUploadProgress, contentType: signedContentType }, abortController)
        .then((response) => {
          // After successful upload to signed URL, handle success
          if (response?.data?.errors?.length) {
            const partialError = new Error(response.data.errors.join(", "));
            partialError.isPartialSuccess = true;
            partialError.response = response;
            throw partialError;
          }

        handleUploadSuccess({
          uploadId,
          updateUpload,
          dispatch,
          matterDetails,
          id,
          uniqueId,
          currentPage,
          setCurrentPage,
          parentId,
          documentId,
          isOfficeFile: false,
          markUploadCompleted,
          aiProcess,
        });

          // Return document ID for final complete API call (for remaining documents)
          return { success: true, documentId, uploadId };
        })
        .catch((error) => {
          handleUploadFailure({
            error,
            uploadId,
            uploads,
            addFailedUpload,
            removeUpload,
            cancelUpload,
            setErrorToastMsgAndShow,
            setisLoading,
            handleSnackbar: null,
            uploadInfo,
            dispatch,
            matterDetails,
            id,
            uniqueId,
            currentPage,
            setCurrentPage,
            parentId,
          });
          return { success: false, documentId: null, uploadId };
        })
        );
      } else if (isOfficeFile && sharepointUrl) {
        // Office files (DOCX, XLSX, etc.): upload to SharePoint URL in chunks
        console.log("Uploading Office file to SharePoint URL in chunks:", file.name);
        uploadPromise = startUploadWithLimit(async () => {
          const { uploadToSharePointInChunks } = await import('../service/uploadService');
          return uploadToSharePointInChunks({ 
          file, 
          sharepointUrl, 
          signal: abortController.signal, 
          onUploadProgress 
        })
        .then((response) => {
          // After successful upload to SharePoint, handle success
          if (response?.data?.errors?.length) {
            const partialError = new Error(response.data.errors.join(", "));
            partialError.isPartialSuccess = true;
            partialError.response = response;
            throw partialError;
          }

        handleUploadSuccess({
          uploadId,
          updateUpload,
          dispatch,
          matterDetails,
          id,
          uniqueId,
          currentPage,
          setCurrentPage,
          parentId,
          documentId,
          isOfficeFile: true,
          markUploadCompleted,
          aiProcess,
        });

          // Return document ID for final complete API call (for remaining documents)
          return { success: true, documentId, uploadId };
        })
        .catch((error) => {
          handleUploadFailure({
            error,
            uploadId,
            uploads,
            addFailedUpload,
            removeUpload,
            cancelUpload,
            setErrorToastMsgAndShow,
            setisLoading,
            handleSnackbar: null,
            uploadInfo,
            dispatch,
            matterDetails,
            id,
            uniqueId,
            currentPage,
            setCurrentPage,
            parentId,
          });
          return { success: false, documentId: null, uploadId };
        });
        });
      } else {
        // Fallback: try signed URL if available
        if (signedUrl) {
          console.log("Uploading file to signed URL (fallback):", file.name);
          uploadPromise = startUploadWithLimit(() => 
            uploadFileToSignedUrl({ file, signedUrl, onUploadProgress, contentType: signedContentType }, abortController)
          .then((response) => {
            if (response?.data?.errors?.length) {
              const partialError = new Error(response.data.errors.join(", "));
              partialError.isPartialSuccess = true;
              partialError.response = response;
              throw partialError;
            }

            handleUploadSuccess({
              uploadId,
              updateUpload,
              dispatch,
              matterDetails,
              id,
              uniqueId,
              currentPage,
              setCurrentPage,
              parentId,
              documentId,
              isOfficeFile: isOfficeFile,
              markUploadCompleted,
              aiProcess,
            });

            return { success: true, documentId, uploadId };
          })
          .catch((error) => {
            handleUploadFailure({
              error,
              uploadId,
              uploads,
              addFailedUpload,
              removeUpload,
              cancelUpload,
              setErrorToastMsgAndShow,
              setisLoading,
              handleSnackbar: null,
              uploadInfo,
              dispatch,
              matterDetails,
              id,
              uniqueId,
              currentPage,
              setCurrentPage,
              parentId,
          });
          return { success: false, documentId: null, uploadId };
          })
          );
        } else {
          // No valid URL available
          const error = new Error(`No upload URL available for file: ${file.name}`);
          handleUploadFailure({
            error,
            uploadId,
            uploads,
            addFailedUpload,
            removeUpload,
            cancelUpload,
            setErrorToastMsgAndShow,
            setisLoading,
            handleSnackbar: null,
            uploadInfo,
            dispatch,
            matterDetails,
            id,
            uniqueId,
            currentPage,
            setCurrentPage,
            parentId,
          });
          uploadPromise = Promise.resolve({ success: false, documentId: null, uploadId });
        }
      }

      uploadPromises.push(uploadPromise);
    } else {
      // Legacy method: fallback to old upload
      const uploadPromise = startUploadWithLimit(() => 
        uploadFile({ ...uploadParams, onUploadProgress }, abortController)
        .then((response) => {
          if (response?.data?.errors?.length) {
            const partialError = new Error(response.data.errors.join(", "));
            partialError.isPartialSuccess = true;
            partialError.response = response;
            throw partialError;
          }

          // Get document ID from response
          const docId = response?.data?.id || response?.data?.document_id || response?.data?.uploaded_documents?.[0]?.id;
          
          // Check file type for legacy upload
          const fileExtension = uploadInfo?.fileName?.split('.').pop()?.toLowerCase();
          const fileType = uploadInfo?.file?.type?.toLowerCase() || '';
          const isOfficeFileLegacy = MS_FILE_TYPES.includes(fileExtension) || MS_FILE_TYPES.includes(fileType) ||
                         fileType.includes('msword') || 
                         fileType.includes('spreadsheetml') ||
                         fileType.includes('wordprocessingml');
          
          handleUploadSuccess({
            uploadId,
            updateUpload,
            dispatch,
            matterDetails,
            id,
            uniqueId,
            currentPage,
            setCurrentPage,
            parentId,
            documentId: docId,
            isOfficeFile: isOfficeFileLegacy,
            markUploadCompleted,
            aiProcess,
          });

          return { success: true, documentId: docId, uploadId };
        })
        .catch((error) => {
          handleUploadFailure({
            error,
            uploadId,
            uploads,
            addFailedUpload,
            removeUpload,
            cancelUpload,
            setErrorToastMsgAndShow,
            setisLoading,
            handleSnackbar: null,
            uploadInfo,
            dispatch,
            matterDetails,
            id,
            uniqueId,
            currentPage,
            setCurrentPage,
            parentId,
          });
          return { success: false, documentId: null, uploadId };
        })
      );

      uploadPromises.push(uploadPromise);
    }
  }

  // Step 3: Wait for all uploads to complete, then call complete API for remaining documents
  // Note: startUploadWithLimit ensures only 10 uploads run concurrently, but all promises
  // are tracked in uploadPromises, so Promise.allSettled will wait for all to complete
  Promise.allSettled(uploadPromises).then(async (results) => {
    // Collect all successful document IDs
    const allDocumentIds = [];
    
    for (const result of results) {
      if (result.status === 'fulfilled' &&
          result.value?.success &&
          result.value?.documentId) {
        allDocumentIds.push(result.value.documentId);
      }
    }

    // Combine with any document IDs still in currentBatchDocumentIds
    const allIds = [...new Set([...currentBatchDocumentIds, ...allDocumentIds])];
    
    // Ensure all document IDs are in uploadedDocIds (in case some were missed)
    allIds.forEach(id => {
      if (!uploadedDocIds.includes(id)) {
        uploadedDocIds.push(id);
      }
    });
    
    // Filter out document IDs that were already completed in batches (if total >= 5)
    // IMPORTANT: Filter BEFORE clearing completedDocumentIds
    const idsToComplete = totalUploads >= BATCH_SIZE 
      ? allIds.filter(id => !completedDocumentIds.has(id))
      : allIds; // If total < 5, complete all IDs (none were completed in batches)
    
    // Mark upload as complete
    isUploadComplete = true;
    console.log(`✅ All uploads completed. Total uploaded document IDs: ${uploadedDocIds.length}`);
    
    // Reset tracking variables for next upload session (but keep uploadedDocIds and optimizedDocIds)
    currentBatchDocumentIds = [];
    completedDocumentIds.clear();
    
    // Call completeUpload API:
    // 1. If total files < 5: call with all document IDs (first time)
    // 2. If total files >= 5: call with remaining document IDs (< 5 remaining)
    // Do NOT call matterDetails/matterDetailsList APIs here - wait for optimization events
    if (idsToComplete.length > 0) {
      try {
        const { completeUpload } = await import('../service/uploadService');
        await completeUpload(idsToComplete, { aiProcess: sessionAiProcess });
        console.log(`✅ CompleteUpload API called ${totalUploads < BATCH_SIZE ? '(all files < 5)' : '(remaining files)'}. Document IDs:`, idsToComplete);
        console.log(`⏳ Waiting for optimization events before calling matterDetails APIs...`);

        // Invalidate IndexedDB cache for any re-uploaded (versioned) documents
        try {
          const { invalidateDocument } = await import('../service/documentCache');
          for (const docId of allIds) {
            await invalidateDocument(String(docId));
          }
        } catch (cacheErr) {
          console.warn('Failed to invalidate document cache after upload:', cacheErr);
        }
      } catch (error) {
        console.error('❌ Failed to call completeUpload API:', error);
        // Don't fail the upload process if complete API fails
      }
    }
    
    // Check if all files are already optimized (e.g., all MS Office files or mix of PDFs and MS files)
    // This handles the case where MS Office files were added to optimizedDocIds immediately
    const totalUploaded = uploadedDocIds.length;
    const totalOptimized = optimizedDocIds.length;
    
    if (totalOptimized === totalUploaded && totalOptimized > 0 && optimizationMatterDetailsConfig) {
      // All files are already optimized (e.g., all MS Office files or all files optimized)
      console.log(`📋 All ${totalOptimized} files are already optimized. Calling matterDetails APIs...`);
      const { dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId } = optimizationMatterDetailsConfig;
      runPagination({ dispatch, matterDetails, id, uniqueId, currentPage, setCurrentPage, parentId });
    } else {
      // Do NOT call list API here - wait for optimization events via WebSocket
      console.log(`📋 Upload complete. Waiting for optimization events to trigger matterDetails APIs...`);
    }
  });
};

// Hook for common upload functionality
export const useUploadUtils = () => {
  const dispatch = useDispatch();

  return {
    dispatch,
    updateCount: () => dispatch(updateCount()),
  };
};

export const extractFilesFromDrop = async (e) => {
  e.preventDefault();

  const items = e.dataTransfer.items;
  const allFiles = [];
  const promises = [];
  const folderNames = [];

  for (let i = 0; i < items.length; i++) {
    const entry = items[i].webkitGetAsEntry?.();
    if (entry) {
      if (entry.isDirectory) {
        folderNames.push(entry.name); // Add all top-level folder names
      }
      promises.push(traverseFileTree(entry, allFiles, ""));
    }
  }

  await Promise.all(promises);

  return { files: allFiles, folderNames };
};


const normalizeFile = (fileOrWrapped) => {
  if (fileOrWrapped.file) {
    return {
      file: fileOrWrapped.file,
      name: fileOrWrapped.file.name,
      webkitRelativePath: fileOrWrapped.webkitRelativePath || null,
      customRelativePath: fileOrWrapped.customRelativePath || null,
      size: fileOrWrapped.file.size,
      emptyFolderName: fileOrWrapped?.file?.emptyFolderName
    };
  } else {
    return {
      file: fileOrWrapped,
      name: fileOrWrapped.name,
      webkitRelativePath: fileOrWrapped.webkitRelativePath || null,
      customRelativePath: fileOrWrapped.customRelativePath || null,
      size: fileOrWrapped.size,
      emptyFolderName: fileOrWrapped?.emptyFolderName
    };
  }
};

const getActualFile = (fileOrWrapped) => {
  if (fileOrWrapped.file) return fileOrWrapped.file; // wrapped object
  return fileOrWrapped; // plain File
};

const getFileMetadata = (fileOrWrapped) => {
  return {
    webkitRelativePath:
      fileOrWrapped.webkitRelativePath ??
      (fileOrWrapped.file?.webkitRelativePath || ""),
    customRelativePath:
      fileOrWrapped.customRelativePath ?? null,
  };
};

export const extractErrorMessage = (error) => {
  // If error is already a string (e.g., from validation), return it directly
  if (typeof error === 'string') {
    return error;
  }
  
  // If error has a message property (Error object or similar), use it
  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }
  
  if (error?.response?.data) {
    const data = error.response.data;

    if (Array.isArray(data.error_type) && data.error_type.length > 0) {
      return data.error_type[0];
    } else if (data.error_type) {
      return data.error_type;
    } else if (data.error) {
      return data.error;
    } else if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0];
    } else if (typeof data.errors === "string") {
      return data.errors;
    }
  }
  return "Failed"; // fallback message
};

// Backend (DRF view in `dms_backend/documents/views/document.py`) responds with
// HTTP 207 + `{ presigned_uploads, errors, error_objects, ... }` when SOME files
// in a batch are accepted and SOME are rejected (filename validation, duplicates,
// unsupported file types, etc.). axios resolves on 2xx including 207, so this
// "partial success" lands in the SUCCESS branch on the frontend, which is exactly
// where we used to throw a generic "Expected N got M" error and discard every
// per-file message the backend already computed.
//
// `partitionPresignedUploadResults` walks the per-file metadata we sent up,
// matches each entry to its presigned-URL response by filename (consuming
// occurrences in order so that two files with the same name in one batch are
// matched in upload order, not collapsed), and produces:
//   - acceptedIndices: original indices of files the backend gave us a URL for
//   - rejectedIndices: original indices of files the backend rejected
//   - rejectionByIndex: original-index -> { message, error_type, filename }
//
// The caller (processFiles / uploadSingleFileWithSignedUrl) uses these to
// (1) mark each rejected upload row as failed with its specific reason, and
// (2) only iterate the accepted files when issuing PUTs to the signed URLs.
export const partitionPresignedUploadResults = (fileMetadata, fileResponses, errorObjects = [], errorStrings = []) => {
  // Defensive normalization. Default-param `= []` only covers `undefined`, not
  // `null`; the backend can serialize a missing field as either, and we
  // dereference these via `for...of` which throws TypeError on null. Coerce
  // anything non-array (null, missing, object, string, etc.) to []. Same
  // pattern as the single-file path in `uploadSingleFileWithSignedUrl`.
  const safeFileMetadata = Array.isArray(fileMetadata) ? fileMetadata : [];
  const safeFileResponses = Array.isArray(fileResponses) ? fileResponses : [];
  const safeErrorObjects = Array.isArray(errorObjects) ? errorObjects : [];
  const safeErrorStrings = Array.isArray(errorStrings) ? errorStrings : [];

  // Map filename -> queue of remaining presigned-URL responses for that filename.
  // Using a queue handles the rare "two files with the same name in one batch"
  // case correctly: we match the first request to the first response, the second
  // request to the second response, instead of both fighting over the same one.
  const acceptedQueues = new Map();
  for (const resp of safeFileResponses) {
    const fname = resp?.filename;
    if (!fname) continue;
    if (!acceptedQueues.has(fname)) {
      acceptedQueues.set(fname, []);
    }
    acceptedQueues.get(fname).push(resp);
  }

  // Same idea for the per-file rejection details.
  const rejectionQueues = new Map();
  for (const errObj of safeErrorObjects) {
    const fname = errObj?.file;
    if (!fname) continue;
    if (!rejectionQueues.has(fname)) {
      rejectionQueues.set(fname, []);
    }
    rejectionQueues.get(fname).push(errObj);
  }

  // Fallback: parse "filename: message" out of the legacy `errors` string array
  // when error_objects is absent (older backend builds). Same queue pattern.
  const errorStringQueues = new Map();
  for (const errStr of safeErrorStrings) {
    if (typeof errStr !== 'string') continue;
    const colonIdx = errStr.indexOf(':');
    if (colonIdx <= 0) continue;
    const fname = errStr.slice(0, colonIdx).trim();
    const msg = errStr.slice(colonIdx + 1).trim();
    if (!fname || !msg) continue;
    if (!errorStringQueues.has(fname)) {
      errorStringQueues.set(fname, []);
    }
    errorStringQueues.get(fname).push(msg);
  }

  const acceptedIndices = [];
  const rejectedIndices = [];
  const rejectionByIndex = new Map();
  const orderedSignedUrlsForAccepted = [];

  for (let i = 0; i < safeFileMetadata.length; i++) {
    const filename = safeFileMetadata[i]?.filename || '';
    const queue = filename ? acceptedQueues.get(filename) : null;
    if (queue && queue.length > 0) {
      acceptedIndices.push(i);
      orderedSignedUrlsForAccepted.push(queue.shift());
      continue;
    }
    rejectedIndices.push(i);
    const rejQueue = filename ? rejectionQueues.get(filename) : null;
    const errStrQueue = filename ? errorStringQueues.get(filename) : null;
    const rejObj = rejQueue && rejQueue.length > 0 ? rejQueue.shift() : null;
    const fallbackMsg = errStrQueue && errStrQueue.length > 0 ? errStrQueue.shift() : null;
    rejectionByIndex.set(i, {
      filename,
      message: rejObj?.message || fallbackMsg || 'Rejected by server (no reason provided)',
      error_type: rejObj?.error_type || null,
    });
  }

  return {
    acceptedIndices,
    rejectedIndices,
    rejectionByIndex,
    orderedSignedUrlsForAccepted,
  };
};

// Builds the one-line-ish toast we show when a batch upload had any rejections.
// Rejected files are grouped by their backend message so the toast looks like:
//
//   "8 of 50 files rejected:
//    • Filename contains unsupported characters (8): A.pdf, B.pdf, C.pdf and 5 more"
//
// We cap the number of filenames per group so a 100-file rejection doesn't blow
// past Slack/antd toast width limits. Caller passes `totalRequested` so we can
// frame the rejection count in context ("8 of 50" reads better than "8 files").
export const buildRejectionSummaryToast = (rejections, totalRequested) => {
  if (!Array.isArray(rejections) || rejections.length === 0) return '';

  const MAX_NAMES_PER_GROUP = 5;
  const grouped = new Map();
  for (const rej of rejections) {
    const key = rej?.message || 'Rejected by server';
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key).push(rej?.filename || 'unknown');
  }

  const lines = [];
  if (typeof totalRequested === 'number' && totalRequested > 0) {
    lines.push(`${rejections.length} of ${totalRequested} files rejected:`);
  } else {
    lines.push(`${rejections.length} file(s) rejected:`);
  }
  for (const [message, names] of grouped.entries()) {
    const shown = names.slice(0, MAX_NAMES_PER_GROUP);
    const extra = names.length - shown.length;
    const namesPart = extra > 0
      ? `${shown.join(', ')} and ${extra} more`
      : shown.join(', ');
    lines.push(`• ${message} (${names.length}): ${namesPart}`);
  }
  return lines.join('\n');
};

export const uploadSingleFileWithSignedUrl = async ({
  file,
  matterDetails,
  uniqueId,
  newly_created = true,
  onUploadProgress,
  signal,
  relativePath = null,
  overrideExisting = false,
  batchId = null,
  completeUploadAfterUpload = true,
  // Whether this upload session asked for AI processing (the AI Claim File
  // Upload route). Only matters when this helper also completes the upload:
  // completion without the marker is a plain filing, never a pipeline run —
  // so a retried claim-file document would otherwise silently skip the AI.
  aiProcess = false,
}) => {
  // Attached to every error thrown from here so callers can offer Retry /
  // rename-and-retry without re-picking the file.
  const retryContext = buildRetryContext({
    file,
    matterDetails,
    uniqueId,
    newly_created,
    relativePath,
    overrideExisting,
    batchId,
    aiProcess,
  });
  try {
    // Prepare file metadata
    const idValue = uniqueId ? getUploadFolderId(uniqueId) : matterDetails.id;
    const uploadMatterId = getUploadMatterId(matterDetails);

    const fileMetadata = [{
      filename: file.name,
      size: file.size,
      matter: uploadMatterId,
      folder: uniqueId ? idValue : null,
      newly_created: newly_created,
      relative_path: relativePath,
      override_existing: overrideExisting,
      batchId,
    }];

    // Step 1: Generate signed URL
    const { generateSignedUrls, uploadToSignedUrl, completeUpload } = await import('../service/uploadService');
    
    const signedUrlsResponse = await generateSignedUrls({
      presigned_files: fileMetadata,
      matter: uploadMatterId,
      newly_created: newly_created,
      override_existing: overrideExisting,
      batchId,
      folder: uniqueId ? idValue : '',
      relative_path: relativePath,
    });

    // Single-file flow: when the backend rejects the one file we sent (filename
    // validation, duplicate, unsupported type, etc.) it returns HTTP 207 with
    // `presigned_uploads = []` plus the per-file rejection in `error_objects` /
    // `errors`. We surface that specific reason to the caller so the UI shows
    // "Filename contains characters that are not allowed" instead of "failed".
    const presignedUploads = signedUrlsResponse?.data?.presigned_uploads;
    const signedUrlData = Array.isArray(presignedUploads)
      ? presignedUploads.find(
        (item) => item.type !== 'folder' &&
          (item.document_id !== undefined || item.id !== undefined || item.unique_id !== undefined || item.upload_url !== undefined)
      )
      : null;

    if (!signedUrlData) {
      const errorObjects = Array.isArray(signedUrlsResponse?.data?.error_objects)
        ? signedUrlsResponse.data.error_objects
        : [];
      const errorStrings = Array.isArray(signedUrlsResponse?.data?.errors)
        ? signedUrlsResponse.data.errors
        : [];
      const matchedErrorObject = errorObjects.find((e) => e?.file === file.name);
      const perFileMsg =
        matchedErrorObject?.message ||
        errorStrings.find((s) => typeof s === 'string' && s.startsWith(`${file.name}:`))?.split(':').slice(1).join(':').trim() ||
        errorObjects[0]?.message ||
        errorStrings[0] ||
        'failed';
      const err = new Error(perFileMsg);
      const errorType = matchedErrorObject?.error_type ?? errorObjects[0]?.error_type ?? null;
      err.errorType = errorType;
      // Same shape handleUploadFailure/renameAndRetryUpload expect — lets a
      // rejection offer Retry (or "rename and retry" for duplicates) even
      // for callers that go through this helper directly (e.g. large-batch
      // individual uploads), not just the processFiles partition path.
      err.retryContext = retryContext;
      throw err;
    }

    const signedUrl = signedUrlData.upload_url;
    const sharepointUrl = signedUrlData.sharepoint_web_url || signedUrlData.sharepoint_url;
    const documentId = signedUrlData.document_id || signedUrlData.id || signedUrlData.unique_id;

    // Step 2: Upload file to signed URL or SharePoint URL based on file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const fileType = file.type?.toLowerCase() || '';
    const isPDF = PDF_FILE_TYPES.includes(fileExtension) || PDF_FILE_TYPES.includes(fileType);
    const isOfficeFile = MS_FILE_TYPES.includes(fileExtension) || MS_FILE_TYPES.includes(fileType) ||
                         fileType.includes('msword') || 
                         fileType.includes('spreadsheetml') ||
                         fileType.includes('wordprocessingml');

    if (isPDF && signedUrl) {
      // PDF files: upload to signed URL
      await uploadToSignedUrl({
        file,
        signedUrl,
        signal,
        onUploadProgress,
      });
    } else if (isOfficeFile && sharepointUrl) {
      // Office files: upload to SharePoint URL in chunks
      const { uploadToSharePointInChunks } = await import('../service/uploadService');
      await uploadToSharePointInChunks({
        file,
        sharepointUrl,
        signal,
        onUploadProgress,
      });
    } else if (signedUrl) {
      // Fallback: use signed URL if available
      await uploadToSignedUrl({
        file,
        signedUrl,
        signal,
        onUploadProgress,
      });
    } else {
      throw new Error(`No upload URL available for file: ${file.name}`);
    }

    // Step 3: Let the caller batch completion for large selections, while
    // preserving the original single-file behavior for existing callers.
    if (documentId) {
      if (!completeUploadAfterUpload) {
        return { data: { success: true }, documentId, isOfficeFile };
      }

      try {
        const completeResponse = await completeUpload([documentId], { aiProcess });
        // Invalidate IndexedDB cache for the uploaded document
        try {
          const { invalidateDocument } = await import('../service/documentCache');
          await invalidateDocument(String(documentId));
        } catch (cacheErr) {
          console.warn('Failed to invalidate document cache after upload:', cacheErr);
        }
        // Preserve the document ID for callers that need to update their
        // upload row after the complete request returns.
        if (completeResponse && typeof completeResponse === 'object') {
          completeResponse.documentId = documentId;
          completeResponse.isOfficeFile = isOfficeFile;
        }
        return completeResponse;
      } catch (completeError) {
        console.error('Failed to call upload complete API:', completeError);
        // Create a proper Error object
  const err = new Error(
    completeError.response?.data?.message ||
    completeError.message ||
    'Failed to complete upload'
  );

  // Attach additional data just like before
  err.response = {
    data: {
      error:
        completeError.response?.data?.message ||
        completeError.message ||
        'Failed to complete upload',
      detail:
        completeError.response?.data?.detail ||
        completeError.message,
    },
  };

  // Throw the Error (valid for linter + preserves stack trace)
  throw err;
      }
    } else {
      // If no documentId, throw error as we cannot complete the upload
     const err = new Error('No document ID available to complete upload');

err.response = {
  data: {
    error: 'No document ID available to complete upload',
    detail: 'Document ID is required to complete the upload process',
  },
};

throw err;
    }
  } catch (error) {
  const err = new Error(
    error.response?.data?.message ||
    error.message ||
    'File upload failed'
  );

  err.response = {
    data: {
      error: error.response?.data?.message || error.message || 'File upload failed',
      detail: error.response?.data?.detail || error.message,
    },
  };

  // Carry forward the per-file rejection metadata attached above (or by a
  // deeper catch) — without this, a duplicate-file rejection's errorType
  // never reaches the caller and every failure looks identical again.
  if (error.errorType !== undefined) {
    err.errorType = error.errorType;
  }
  // Network drops and server errors surface here with no context of their
  // own; every error leaving this helper carries the bytes + destination.
  err.retryContext = error.retryContext ?? retryContext;

  throw err;
}
};



